import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import formService from "../../services/formService";
import { productSearchService } from "../../services/productSearchService";
import { formatCurrencyWithCommas } from "../../utils/numberUtils";
import {
  loadAlphaManagedStrategyDataByProductId,
  type AlphaManagedStrategyPerformanceData,
} from "./alphaRecommendationData";
import type { AlphaIbkrAccountType } from "./alphaIbkrAccountDetails";
import { resolveAlphaManagedMatch } from "./alphaManagedMatch";
import { getAlphaManagedStrategyByMixId } from "./alphaManagedStrategyResolver";
import {
  getAlphaMixFloorUsd,
  isAlphaMixId,
  resolveAlphaMixId,
  type AlphaRiskKey,
  type AlphaRiskTrack,
} from "./alphaRiskMap";

// Sizing engine for a partial Plutus Managed allocation. The managed mix is
// matched to the dollars Managed actually runs (weight x account value), so a
// weight edit can cross an amount band and require a different managed product.
// This hook owns that resolution:
//   weight and product unchanged   -> saved product (never rematch on open)
//   product replaced at same weight -> rematch; do not snap back to saved
//   resolved mix == held mix       -> saved product
//   otherwise                      -> product looked up by the resolved mix
// plus the floor gate (below the smallest band nothing qualifies) and the
// informational perf load for the strip/preview. Every async write is guarded
// by a monotonic request token so an out-of-order response can never land.

export interface AlphaManagedSizingInput {
  open: boolean;
  isCustomer: boolean;
  nlv: number | null;
  /**
   * Optional resolved account type override. When set it wins over the
   * questionnaire answer for risk-track resolution. Null lets
   * resolveAlphaManagedMatch read the questionnaire directly.
   */
  configAccountType: AlphaIbkrAccountType | null;
  managedProductId: number | null;
  managedWeight: number | null;
  savedManagedProductId: number | null;
  savedManagedWeight: number | null;
  onSwapProduct: (fromProductId: number, toProductId: number) => void;
}

export type AlphaManagedPerfStatus = "idle" | "loading" | "ready" | "error";

export interface AlphaManagedPerf {
  status: AlphaManagedPerfStatus;
  data: AlphaManagedStrategyPerformanceData | null;
}

export interface AlphaManagedSizing {
  /** Dollar-based sizing is active: customer, NLV known, risk profile resolved. */
  enabled: boolean;
  dollars: number | null;
  floorUsd: number | null;
  /** Smallest whole weight percent whose dollars clear the floor. */
  floorWeightPct: number | null;
  belowFloor: boolean;
  managedWeightChanged: boolean;
  /** Applying will switch the managed product (mix rematch). */
  rematchPending: boolean;
  /** Why apply must stay disabled, or null when it may proceed. */
  blockedReason: string | null;
  /** A needed mix resolution is still in flight. */
  resolutionPending: boolean;
  resolutionFailed: boolean;
  perf: AlphaManagedPerf;
  /** Held product's same-window return when already loaded (preview context line). */
  heldSameWindowReturnPct: number | null;
  retryResolution: () => void;
  retryPerf: () => void;
}

type ProfileState =
  | { status: "idle" | "loading" | "unavailable" }
  | { status: "ready"; riskTrack: AlphaRiskTrack; riskKey: AlphaRiskKey };

type HeldMixState =
  | { status: "idle" | "loading" | "error" }
  | { status: "ready"; mixId: string | null };

type MixResolutionState =
  | { status: "idle" | "pending" | "error" }
  | { status: "ready"; mixId: string; productId: number };

export function useAlphaManagedSizing(input: AlphaManagedSizingInput): AlphaManagedSizing {
  const {
    open,
    isCustomer,
    nlv,
    configAccountType,
    managedProductId,
    managedWeight,
    savedManagedProductId,
    savedManagedWeight,
    onSwapProduct,
  } = input;

  const [profile, setProfile] = useState<ProfileState>({ status: "idle" });
  const [heldMix, setHeldMix] = useState<HeldMixState>({ status: "idle" });
  const [mixResolution, setMixResolution] = useState<MixResolutionState>({ status: "idle" });
  const [perf, setPerf] = useState<AlphaManagedPerf>({ status: "idle", data: null });
  const [resolutionRetryTick, setResolutionRetryTick] = useState(0);
  const [perfRetryTick, setPerfRetryTick] = useState(0);

  const profileReqRef = useRef(0);
  const heldReqRef = useRef(0);
  const mixReqRef = useRef(0);
  const perfReqRef = useRef(0);
  const mixProductCacheRef = useRef(new Map<string, number>());
  const perfCacheRef = useRef(new Map<number, AlphaManagedStrategyPerformanceData>());

  // ---- Risk profile (once per open) -------------------------------------
  useEffect(() => {
    if (!open || !isCustomer) {
      profileReqRef.current += 1;
      setProfile({ status: "idle" });
      return;
    }
    const requestId = ++profileReqRef.current;
    setProfile({ status: "loading" });
    void (async () => {
      try {
        const [questions, responses] = await Promise.all([
          formService.listQuestions(),
          formService.getMyResponses(),
        ]);
        if (requestId !== profileReqRef.current) return;
        const match = resolveAlphaManagedMatch(
          questions,
          responses,
          configAccountType
        );
        if (match.status !== "ready") {
          setProfile({ status: "unavailable" });
          return;
        }
        setProfile({
          status: "ready",
          riskTrack: match.selection.riskTrack,
          riskKey: match.riskKey,
        });
      } catch {
        if (requestId !== profileReqRef.current) return;
        // Fail open: sizing degrades and the editor behaves exactly as it does
        // today (same product, no floor gate). See spec section 4.6.
        setProfile({ status: "unavailable" });
      }
    })();
    return () => {
      profileReqRef.current += 1;
    };
  }, [open, isCustomer, configAccountType]);

  // ---- Held product's mix (fast path for "band unchanged") ---------------
  useEffect(() => {
    if (!open || !isCustomer || savedManagedProductId == null) {
      heldReqRef.current += 1;
      setHeldMix({ status: "idle" });
      return;
    }
    const requestId = ++heldReqRef.current;
    const controller = new AbortController();
    setHeldMix({ status: "loading" });
    void (async () => {
      try {
        const item = await productSearchService.getManagedStrategyForInvestor(
          savedManagedProductId,
          controller.signal
        );
        if (requestId !== heldReqRef.current) return;
        const mixId = "mixId" in item.settings ? item.settings.mixId : null;
        setHeldMix({ status: "ready", mixId: isAlphaMixId(mixId) ? mixId : null });
      } catch {
        if (requestId !== heldReqRef.current) return;
        // Unknown held mix: the by-mix lookup below still resolves the target.
        setHeldMix({ status: "error" });
      }
    })();
    return () => {
      heldReqRef.current += 1;
      controller.abort();
    };
  }, [open, isCustomer, savedManagedProductId]);

  // ---- Synchronous sizing math -------------------------------------------
  const enabled = open && isCustomer && nlv != null && profile.status === "ready";
  const weight = managedWeight ?? 0;
  const hasManagedRow = managedProductId != null;

  const floorUsd = useMemo(() => {
    if (!isCustomer || profile.status !== "ready") return null;
    return getAlphaMixFloorUsd(profile.riskTrack, profile.riskKey);
  }, [isCustomer, profile]);

  const floorWeightPct = useMemo(() => {
    if (floorUsd == null || nlv == null || nlv <= 0) return null;
    return Math.min(100, Math.ceil((floorUsd / nlv) * 100));
  }, [floorUsd, nlv]);

  const dollars = enabled && hasManagedRow
    ? Math.max(0, Math.round((weight / 100) * (nlv as number)))
    : null;

  const belowFloor = enabled
    && hasManagedRow
    && weight > 0
    && dollars != null
    && floorUsd != null
    && dollars < floorUsd;

  const managedWeightChanged = hasManagedRow
    && (savedManagedWeight == null || weight !== savedManagedWeight);
  // Adopt can replace the managed product at the same seed weight. Weight-only
  // change detection then treated that as "unchanged" and snapped the draft
  // back to the saved product.
  const managedProductChanged = hasManagedRow
    && savedManagedProductId != null
    && managedProductId !== savedManagedProductId;
  const managedAllocationChanged = managedWeightChanged || managedProductChanged;

  const resolvedMixId = useMemo(() => {
    if (!enabled || !hasManagedRow || weight <= 0 || belowFloor || dollars == null) {
      return null;
    }
    if (profile.status !== "ready") return null;
    try {
      return resolveAlphaMixId(profile.riskTrack, profile.riskKey, dollars);
    } catch {
      return null;
    }
  }, [enabled, hasManagedRow, weight, belowFloor, dollars, profile]);

  const needsResolution = enabled && managedAllocationChanged && weight > 0 && !belowFloor;

  // ---- Target product lookup by resolved mix (slow path) -----------------
  useEffect(() => {
    if (!needsResolution || resolvedMixId == null) {
      mixReqRef.current += 1;
      setMixResolution({ status: "idle" });
      return;
    }
    // The explicitly held product wins when it already represents the newly
    // resolved mix. Wait for that exact-product lookup before consulting a
    // strict live Mix-ID match; otherwise a faster Mix lookup can
    // replace the held ID while its metadata is still loading.
    if (
      savedManagedProductId != null
      && (heldMix.status === "idle" || heldMix.status === "loading")
    ) {
      mixReqRef.current += 1;
      setMixResolution({ status: "pending" });
      return;
    }
    // Fast path: the resolved mix is the held one, so the saved product stands.
    if (
      savedManagedProductId != null
      && heldMix.status === "ready"
      && heldMix.mixId === resolvedMixId
    ) {
      mixReqRef.current += 1;
      setMixResolution({
        status: "ready",
        mixId: resolvedMixId,
        productId: savedManagedProductId,
      });
      return;
    }
    const cached = mixProductCacheRef.current.get(resolvedMixId);
    if (cached != null) {
      mixReqRef.current += 1;
      setMixResolution({ status: "ready", mixId: resolvedMixId, productId: cached });
      return;
    }
    const requestId = ++mixReqRef.current;
    const controller = new AbortController();
    setMixResolution({ status: "pending" });
    void (async () => {
      try {
        const item = await getAlphaManagedStrategyByMixId(
          resolvedMixId,
          controller.signal
        );
        if (requestId !== mixReqRef.current) return;
        mixProductCacheRef.current.set(resolvedMixId, item.productId);
        setMixResolution({ status: "ready", mixId: resolvedMixId, productId: item.productId });
      } catch {
        if (requestId !== mixReqRef.current || controller.signal.aborted) return;
        setMixResolution({ status: "error" });
      }
    })();
    return () => {
      mixReqRef.current += 1;
      controller.abort();
    };
  }, [needsResolution, resolvedMixId, savedManagedProductId, heldMix, resolutionRetryTick]);

  // ---- Target product invariant ------------------------------------------
  const targetProductId = useMemo(() => {
    if (!hasManagedRow) return null;
    if (!enabled) return managedProductId;
    if (!managedAllocationChanged) return savedManagedProductId ?? managedProductId;
    if (weight <= 0 || belowFloor) return managedProductId;
    if (
      savedManagedProductId != null
      && heldMix.status === "ready"
      && heldMix.mixId != null
      && heldMix.mixId === resolvedMixId
    ) {
      return savedManagedProductId;
    }
    if (mixResolution.status === "ready" && mixResolution.mixId === resolvedMixId) {
      return mixResolution.productId;
    }
    return null;
  }, [
    hasManagedRow, enabled, managedProductId, managedAllocationChanged,
    savedManagedProductId, weight, belowFloor, heldMix, resolvedMixId, mixResolution,
  ]);

  // Keep the drafted row's product aligned with the target. Fires only when
  // they differ; the parent's swap makes them equal, so this cannot loop.
  useEffect(() => {
    if (managedProductId == null || targetProductId == null) return;
    if (managedProductId === targetProductId) return;
    onSwapProduct(managedProductId, targetProductId);
  }, [managedProductId, targetProductId, onSwapProduct]);

  const rematchPending = needsResolution
    && savedManagedProductId != null
    && targetProductId != null
    && targetProductId !== savedManagedProductId;

  // ---- Informational perf load (never blocks apply) ----------------------
  const perfProductId = enabled && hasManagedRow && weight > 0 && !belowFloor
    ? targetProductId
    : null;

  useEffect(() => {
    if (perfProductId == null) {
      perfReqRef.current += 1;
      setPerf({ status: "idle", data: null });
      return;
    }
    const cached = perfCacheRef.current.get(perfProductId);
    if (cached) {
      perfReqRef.current += 1;
      setPerf({ status: "ready", data: cached });
      return;
    }
    const requestId = ++perfReqRef.current;
    const controller = new AbortController();
    setPerf({ status: "loading", data: null });
    void (async () => {
      try {
        const data = await loadAlphaManagedStrategyDataByProductId(
          perfProductId,
          controller.signal
        );
        if (requestId !== perfReqRef.current) return;
        perfCacheRef.current.set(perfProductId, data);
        setPerf({ status: "ready", data });
      } catch {
        if (requestId !== perfReqRef.current || controller.signal.aborted) return;
        setPerf({ status: "error", data: null });
      }
    })();
    return () => {
      perfReqRef.current += 1;
      controller.abort();
    };
  }, [perfProductId, perfRetryTick]);

  const heldSameWindowReturnPct = savedManagedProductId != null
    ? perfCacheRef.current.get(savedManagedProductId)?.strategyReturnPct ?? null
    : null;

  const blockedReason = useMemo(() => {
    if (!enabled || !hasManagedRow || !managedAllocationChanged || weight <= 0) return null;
    if (belowFloor && floorUsd != null) {
      return `Plutus Managed is below its ${formatCurrencyWithCommas(floorUsd, 0)} minimum`;
    }
    if (targetProductId == null) {
      return mixResolution.status === "error"
        ? "We couldn't match your managed mix. Try again."
        : "Matching your managed mix";
    }
    return null;
  }, [
    enabled, hasManagedRow, managedAllocationChanged, weight, belowFloor, floorUsd,
    targetProductId, mixResolution.status,
  ]);

  const retryResolution = useCallback(() => setResolutionRetryTick((t) => t + 1), []);
  const retryPerf = useCallback(() => setPerfRetryTick((t) => t + 1), []);

  return {
    enabled,
    dollars,
    floorUsd,
    floorWeightPct,
    belowFloor,
    managedWeightChanged,
    rematchPending,
    blockedReason,
    resolutionPending: needsResolution && targetProductId == null && mixResolution.status !== "error",
    resolutionFailed: mixResolution.status === "error",
    perf,
    heldSameWindowReturnPct,
    retryResolution,
    retryPerf,
  };
}
